import axios from 'axios';
import { authHeaders } from './auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/marketing`;

// ---- Public ----
export async function createLead(payload) {
  return (await axios.post(`${API}/leads`, payload)).data;
}
export async function subscribe(payload) {
  return (await axios.post(`${API}/subscribe`, payload)).data;
}
export async function signupCheckout(payload) {
  return (await axios.post(`${API}/signup/checkout`, payload)).data;
}
export async function signupStatus(sessionId) {
  return (await axios.get(`${API}/signup/status/${sessionId}`)).data;
}
export async function signupFree(payload) {
  return (await axios.post(`${API}/signup/free`, payload)).data;
}

// ---- Leads CRM (admin) ----
export async function listLeads(params = {}) {
  return (await axios.get(`${API}/leads`, { params, headers: authHeaders() })).data;
}
export async function leadStats() {
  return (await axios.get(`${API}/leads/stats`, { headers: authHeaders() })).data;
}
export async function updateLead(id, payload) {
  return (await axios.patch(`${API}/leads/${id}`, payload, { headers: authHeaders() })).data;
}
export function exportLeadsUrl() {
  return `${API}/leads/export`;
}
export async function hideLead(id, reason) {
  return (await axios.post(`${API}/leads/${id}/hide`, { reason }, { headers: authHeaders() })).data;
}
export async function restoreLead(id) {
  return (await axios.post(`${API}/leads/${id}/restore`, {}, { headers: authHeaders() })).data;
}
export async function deleteLead(id) {
  return (await axios.delete(`${API}/leads/${id}`, { headers: authHeaders() })).data;
}

// ---- Partners (public apply + admin CRM) ----
export async function applyPartner(payload) {
  return (await axios.post(`${API}/partners/apply`, payload)).data;
}
export async function listPartners(params = {}) {
  return (await axios.get(`${API}/partners`, { params, headers: authHeaders() })).data;
}
export async function partnerStats() {
  return (await axios.get(`${API}/partners/stats`, { headers: authHeaders() })).data;
}
export async function getPartner(id) {
  return (await axios.get(`${API}/partners/${id}`, { headers: authHeaders() })).data;
}
export async function updatePartner(id, payload) {
  return (await axios.patch(`${API}/partners/${id}`, payload, { headers: authHeaders() })).data;
}
export async function addPartnerNote(id, text) {
  return (await axios.post(`${API}/partners/${id}/notes`, { text }, { headers: authHeaders() })).data;
}
export async function enrichPartner(id) {
  return postWithRetry(`${API}/partners/${id}/enrich`, {});
}
export async function sendPartnerBookingLink(id, payload = {}) {
  return postWithRetry(`${API}/partners/${id}/send-booking-link`, payload);
}
export function exportPartnersUrl() {
  return `${API}/partners/export`;
}
export async function hidePartner(id, reason) {
  return (await axios.post(`${API}/partners/${id}/hide`, { reason }, { headers: authHeaders() })).data;
}
export async function restorePartner(id) {
  return (await axios.post(`${API}/partners/${id}/restore`, {}, { headers: authHeaders() })).data;
}
export async function deletePartner(id) {
  return (await axios.delete(`${API}/partners/${id}`, { headers: authHeaders() })).data;
}

// ---- User management (super admin) ----
export async function listUsers() {
  return (await axios.get(`${API}/users`, { headers: authHeaders() })).data;
}
export async function createUser(payload) {
  return (await axios.post(`${API}/users`, payload, { headers: authHeaders() })).data;
}
export async function updateUser(id, payload) {
  return (await axios.patch(`${API}/users/${id}`, payload, { headers: authHeaders() })).data;
}
export async function deleteUser(id) {
  return (await axios.delete(`${API}/users/${id}`, { headers: authHeaders() })).data;
}
export async function listLockedAccounts() {
  return (await axios.get(`${API}/users/locked`, { headers: authHeaders() })).data;
}
export async function unlockUser(userId) {
  return (await axios.post(`${API}/users/${userId}/unlock`, {}, { headers: authHeaders() })).data;
}

// ---- Enquiry detail / notes / AI enrichment ----
export async function getLead(id) {
  return (await axios.get(`${API}/leads/${id}`, { headers: authHeaders() })).data;
}
export async function addNote(id, text) {
  return (await axios.post(`${API}/leads/${id}/notes`, { text }, { headers: authHeaders() })).data;
}
export async function enrichLead(id) {
  return (await axios.post(`${API}/leads/${id}/enrich`, {}, { headers: authHeaders() })).data;
}

// ---- Dashboard ----
export async function dashboardStats() {
  return (await axios.get(`${API}/dashboard/stats`, { headers: authHeaders() })).data;
}

// ---- CMS: public reads ----
export async function fetchBlogs() {
  return (await axios.get(`${API}/content/blogs`)).data;
}
export async function fetchBlog(slug) {
  return (await axios.get(`${API}/content/blogs/${slug}`)).data;
}
export async function fetchCaseStudies() {
  return (await axios.get(`${API}/content/case-studies`)).data;
}
export async function fetchCaseStudy(slug) {
  return (await axios.get(`${API}/content/case-studies/${slug}`)).data;
}

// ---- CMS: admin CRUD (blogs / case-studies) ----
const seg = (type) => (type === 'case_study' ? 'case-studies' : 'blogs');

export async function cmsList(type, status) {
  const params = status ? { status } : {};
  return (await axios.get(`${API}/admin/content/${seg(type)}`, { params, headers: authHeaders() })).data;
}
export async function cmsGet(type, id) {
  return (await axios.get(`${API}/admin/content/${seg(type)}/${id}`, { headers: authHeaders() })).data;
}
export async function cmsCreate(type, payload) {
  return (await axios.post(`${API}/admin/content/${seg(type)}`, payload, { headers: authHeaders() })).data;
}
export async function cmsUpdate(type, id, payload) {
  return (await axios.put(`${API}/admin/content/${seg(type)}/${id}`, payload, { headers: authHeaders() })).data;
}
export async function cmsSetStatus(type, id, status, shareLinkedin) {
  const body = { status };
  if (shareLinkedin !== undefined) body.share_linkedin = shareLinkedin;
  return (await axios.post(`${API}/admin/content/${seg(type)}/${id}/status`, body, { headers: authHeaders() })).data;
}
export async function cmsDelete(type, id) {
  return (await axios.delete(`${API}/admin/content/${seg(type)}/${id}`, { headers: authHeaders() })).data;
}
// Retry once on a transient gateway hiccup (e.g. first cold-start 502) before surfacing the error.
async function postWithRetry(url, body) {
  try {
    return (await axios.post(url, body, { headers: authHeaders(), timeout: 90000 })).data;
  } catch (e) {
    const s = e.response?.status;
    if (s === 502 || s === 503 || s === 504 || !e.response) {
      await new Promise((r) => setTimeout(r, 1500));
      return (await axios.post(url, body, { headers: authHeaders(), timeout: 90000 })).data;
    }
    throw e;
  }
}
export async function cmsAIWrite(payload) {
  return postWithRetry(`${API}/admin/content/ai/write`, payload);
}
export async function cmsAIImage(prompt) {
  return postWithRetry(`${API}/admin/content/ai/image`, { prompt });
}
export async function cmsAISeo(payload) {
  return postWithRetry(`${API}/admin/content/ai/seo`, payload);
}
export async function cmsUploadImage(file) {
  const fd = new FormData();
  fd.append('file', file);
  return (await axios.post(`${API}/admin/content/upload-image`, fd, { headers: { ...authHeaders() } })).data;
}

// ---- LinkedIn social ----
export async function linkedinStatus() {
  return (await axios.get(`${API}/admin/social/linkedin/status`, { headers: authHeaders() })).data;
}
export async function linkedinOAuthStart() {
  return (await axios.get(`${API}/admin/social/linkedin/oauth/start`, { headers: authHeaders() })).data;
}
export async function linkedinDisconnect() {
  return (await axios.post(`${API}/admin/social/linkedin/disconnect`, {}, { headers: authHeaders() })).data;
}
export async function linkedinCaption(type, id, extra) {
  return postWithRetry(`${API}/admin/content/${seg(type)}/${id}/linkedin-caption`, { extra });
}
export async function linkedinShare(type, id, payload) {
  return postWithRetry(`${API}/admin/content/${seg(type)}/${id}/share-linkedin`, payload);
}
export async function linkedinHistory(type, id) {
  return (await axios.get(`${API}/admin/content/${seg(type)}/${id}/social`, { headers: authHeaders() })).data;
}

// ---- Newsletter: public ----
export async function fetchNewsletterCategories() {
  return (await axios.get(`${API}/newsletter/categories`)).data;
}
export async function subscribeNewsletter(payload) {
  return (await axios.post(`${API}/newsletter/subscribe`, payload)).data;
}
export async function unsubscribeNewsletter(token, category) {
  return (await axios.post(`${API}/newsletter/unsubscribe`, { token, category })).data;
}

// ---- Newsletter: admin ----
export async function nlSubscribers(params = {}) {
  return (await axios.get(`${API}/admin/newsletter/subscribers`, { params, headers: authHeaders() })).data;
}
export async function nlSubscriberStats() {
  return (await axios.get(`${API}/admin/newsletter/subscribers/stats`, { headers: authHeaders() })).data;
}
export async function nlList(params = {}) {
  return (await axios.get(`${API}/admin/newsletter`, { params, headers: authHeaders() })).data;
}
export async function nlGet(id) {
  return (await axios.get(`${API}/admin/newsletter/${id}`, { headers: authHeaders() })).data;
}
export async function nlCreate(payload) {
  return (await axios.post(`${API}/admin/newsletter`, payload, { headers: authHeaders() })).data;
}
export async function nlUpdate(id, payload) {
  return (await axios.put(`${API}/admin/newsletter/${id}`, payload, { headers: authHeaders() })).data;
}
export async function nlDelete(id) {
  return (await axios.delete(`${API}/admin/newsletter/${id}`, { headers: authHeaders() })).data;
}
export async function nlAIDraft(payload) {
  return postWithRetry(`${API}/admin/newsletter/ai-draft`, payload);
}
export async function nlSend(id, payload = {}) {
  return postWithRetry(`${API}/admin/newsletter/${id}/send`, payload);
}

// ---- Unified CRM Contacts ----
export async function crmContacts(params = {}) {
  return (await axios.get(`${API}/admin/contacts`, { params, headers: authHeaders() })).data;
}
export async function crmContactStats() {
  return (await axios.get(`${API}/admin/contacts/stats`, { headers: authHeaders() })).data;
}
export async function crmContact(id) {
  return (await axios.get(`${API}/admin/contacts/${id}`, { headers: authHeaders() })).data;
}
export async function crmUpdateContact(id, body) {
  return (await axios.patch(`${API}/admin/contacts/${id}`, body, { headers: authHeaders() })).data;
}
export async function hideContact(id, reason) {
  return (await axios.post(`${API}/admin/contacts/${id}/hide`, { reason }, { headers: authHeaders() })).data;
}
export async function restoreContact(id) {
  return (await axios.post(`${API}/admin/contacts/${id}/restore`, {}, { headers: authHeaders() })).data;
}
export async function deleteContact(id) {
  return (await axios.delete(`${API}/admin/contacts/${id}`, { headers: authHeaders() })).data;
}

// ---- Cold-lead retargeting ----
export async function retargetingStats() {
  return (await axios.get(`${API}/admin/retargeting/stats`, { headers: authHeaders() })).data;
}
export async function retargetingColdLeads(params = {}) {
  return (await axios.get(`${API}/admin/retargeting/cold-leads`, { params, headers: authHeaders() })).data;
}
export async function retargetingReengage(cid) {
  return postWithRetry(`${API}/admin/retargeting/reengage/${cid}`, {});
}
export async function retargetingRun() {
  return postWithRetry(`${API}/admin/retargeting/run`, {});
}

// ---- Companies CRM ----
export async function crmCompanies(params = {}) {
  return (await axios.get(`${API}/admin/companies`, { params, headers: authHeaders() })).data;
}
export async function crmCompanyStats() {
  return (await axios.get(`${API}/admin/companies/stats`, { headers: authHeaders() })).data;
}
export async function crmCompany(id) {
  return (await axios.get(`${API}/admin/companies/${id}`, { headers: authHeaders() })).data;
}
export async function crmUpdateCompany(id, body) {
  return (await axios.patch(`${API}/admin/companies/${id}`, body, { headers: authHeaders() })).data;
}
export async function crmEnrichCompany(id) {
  return postWithRetry(`${API}/admin/companies/${id}/enrich`, {});
}
export async function hideCompany(id, reason) {
  return (await axios.post(`${API}/admin/companies/${id}/hide`, { reason }, { headers: authHeaders() })).data;
}
export async function restoreCompany(id) {
  return (await axios.post(`${API}/admin/companies/${id}/restore`, {}, { headers: authHeaders() })).data;
}
export async function deleteCompany(id) {
  return (await axios.delete(`${API}/admin/companies/${id}`, { headers: authHeaders() })).data;
}

// ---- Retargeting AI drafts ----
export async function retargetDraftsGenerate(payload = {}) {
  return postWithRetry(`${API}/admin/retargeting/drafts/generate`, payload);
}
export async function retargetDraftsList(params = {}) {
  return (await axios.get(`${API}/admin/retargeting/drafts`, { params, headers: authHeaders() })).data;
}
export async function retargetDraftUpdate(id, body) {
  return (await axios.patch(`${API}/admin/retargeting/drafts/${id}`, body, { headers: authHeaders() })).data;
}
export async function retargetDraftDelete(id) {
  return (await axios.delete(`${API}/admin/retargeting/drafts/${id}`, { headers: authHeaders() })).data;
}
export async function retargetDraftsSend(draftIds) {
  return postWithRetry(`${API}/admin/retargeting/drafts/send`, { draft_ids: draftIds });
}

// ---- Subscriptions CRM ----
export async function subsList(params = {}) {
  return (await axios.get(`${API}/admin/subscriptions`, { params, headers: authHeaders() })).data;
}
export async function subsStats() {
  return (await axios.get(`${API}/admin/subscriptions/stats`, { headers: authHeaders() })).data;
}
export async function subsGet(id) {
  return (await axios.get(`${API}/admin/subscriptions/${id}`, { headers: authHeaders() })).data;
}
export async function subsUpdate(id, body) {
  return (await axios.patch(`${API}/admin/subscriptions/${id}`, body, { headers: authHeaders() })).data;
}
export async function subsAiSummary(id) {
  return postWithRetry(`${API}/admin/subscriptions/${id}/ai-summary`, {});
}
export async function subsBulkHide(ids, reason) {
  return (await axios.post(`${API}/admin/subscriptions/bulk-hide`, { ids, reason }, { headers: authHeaders() })).data;
}
export async function leadsBulkHide(ids, reason) {
  return (await axios.post(`${API}/leads/bulk-hide`, { ids, reason }, { headers: authHeaders() })).data;
}
export async function companiesBulkHide(ids, reason) {
  return (await axios.post(`${API}/admin/companies/bulk-hide`, { ids, reason }, { headers: authHeaders() })).data;
}
export async function subsBulkRestore(ids) {
  return (await axios.post(`${API}/admin/subscriptions/bulk-restore`, { ids }, { headers: authHeaders() })).data;
}
export async function subsBulkAssign(ids, owner) {
  return (await axios.post(`${API}/admin/subscriptions/bulk-assign`, { ids, owner }, { headers: authHeaders() })).data;
}
export async function leadsBulkRestore(ids) {
  return (await axios.post(`${API}/leads/bulk-restore`, { ids }, { headers: authHeaders() })).data;
}
export async function companiesBulkRestore(ids) {
  return (await axios.post(`${API}/admin/companies/bulk-restore`, { ids }, { headers: authHeaders() })).data;
}
export async function companiesBulkAssign(ids, owner) {
  return (await axios.post(`${API}/admin/companies/bulk-assign`, { ids, owner }, { headers: authHeaders() })).data;
}
export async function hideSubscription(id, reason) {
  return (await axios.post(`${API}/admin/subscriptions/${id}/hide`, { reason }, { headers: authHeaders() })).data;
}
export async function restoreSubscription(id) {
  return (await axios.post(`${API}/admin/subscriptions/${id}/restore`, {}, { headers: authHeaders() })).data;
}
export async function deleteSubscription(id) {
  return (await axios.delete(`${API}/admin/subscriptions/${id}`, { headers: authHeaders() })).data;
}
export async function subsPullNow(full = false) {
  return (await axios.post(`${API}/admin/subscriptions/pull-now?full=${full}`, {}, { headers: authHeaders() })).data;
}
export async function subsPullStatus() {
  return (await axios.get(`${API}/admin/subscriptions/pull-status`, { headers: authHeaders() })).data;
}
export async function subsHidePreCutoff() {
  return (await axios.post(`${API}/admin/subscriptions/hide-pre-cutoff`, {}, { headers: authHeaders() })).data;
}

// ---- Booking & scheduling ----
export async function bookingConfig() {
  return (await axios.get(`${API}/booking/config`)).data;
}
export async function getBookingSettings() {
  return (await axios.get(`${API}/admin/settings/booking`, { headers: authHeaders() })).data;
}
export async function saveBookingSettings(payload) {
  return (await axios.put(`${API}/admin/settings/booking`, payload, { headers: authHeaders() })).data;
}
export async function sendBookingLink(leadId, payload = {}) {
  return postWithRetry(`${API}/leads/${leadId}/send-booking-link`, payload);
}

// ---- Meetings on a contact ----
export async function listMeetings(cid) {
  return (await axios.get(`${API}/admin/contacts/${cid}/meetings`, { headers: authHeaders() })).data;
}
export async function createMeeting(cid, payload) {
  return (await axios.post(`${API}/admin/contacts/${cid}/meetings`, payload, { headers: authHeaders() })).data;
}
export async function updateMeeting(mid, payload) {
  return (await axios.patch(`${API}/admin/meetings/${mid}`, payload, { headers: authHeaders() })).data;
}
export async function deleteMeeting(mid) {
  return (await axios.delete(`${API}/admin/meetings/${mid}`, { headers: authHeaders() })).data;
}
export async function contactColleagues(cid) {
  return (await axios.get(`${API}/admin/contacts/${cid}/colleagues`, { headers: authHeaders() })).data;
}

// Resolve a stored image ref to an absolute URL (media paths are backend-relative).
export function resolveImg(url) {
  if (!url) return url;
  return url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
}

// ---- Public analytics (fire-and-forget, no auth) ----
export function trackContent(type, slug, event) {
  const s = type === 'case_study' ? 'case-studies' : 'blogs';
  try {
    axios.post(`${API}/content/${s}/${slug}/track`, { event }).catch(() => {});
  } catch (e) { /* noop */ }
}

function _sessionId() {
  try {
    let sid = sessionStorage.getItem('bp_mkt_sid');
    if (!sid) {
      sid = (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      sessionStorage.setItem('bp_mkt_sid', sid);
    }
    return sid;
  } catch (e) {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

export function trackPageview(path, title) {
  try {
    const params = new URLSearchParams(window.location.search);
    axios.post(`${API}/track/pageview`, {
      session_id: _sessionId(),
      path: path || window.location.pathname,
      title: title || document.title || '',
      referrer: document.referrer || '',
      utm_source: params.get('utm_source') || null,
      utm_medium: params.get('utm_medium') || null,
      utm_campaign: params.get('utm_campaign') || null,
      language: (localStorage.getItem('bp_marketing_lang') || 'en'),
    }).catch(() => {});
  } catch (e) { /* noop */ }
}

// ---- Marketing analytics dashboard (admin) ----
export async function analyticsOverview(days = 30) {
  return (await axios.get(`${API}/analytics/overview`, { params: { days }, headers: authHeaders() })).data;
}
export async function analyticsVisitors(days = 30, limit = 50) {
  return (await axios.get(`${API}/analytics/visitors`, { params: { days, limit }, headers: authHeaders() })).data;
}
export async function reportSettings() {
  return (await axios.get(`${API}/admin/analytics/report-settings`, { headers: authHeaders() })).data;
}
export async function saveReportSettings(payload) {
  return (await axios.put(`${API}/admin/analytics/report-settings`, payload, { headers: authHeaders() })).data;
}
export async function sendReportNow() {
  return (await axios.post(`${API}/admin/analytics/send-report-now`, {}, { headers: authHeaders() })).data;
}

// ---- Team, engagement, campaigns, bulk email, export ----
export async function crmTeam() {
  return (await axios.get(`${API}/admin/team`, { headers: authHeaders() })).data;
}
export async function engagementSummary() {
  return (await axios.get(`${API}/admin/engagement/summary`, { headers: authHeaders() })).data;
}
export async function engagementAlertSettings() {
  return (await axios.get(`${API}/admin/engagement/alert-settings`, { headers: authHeaders() })).data;
}
export async function saveEngagementAlertSettings(payload) {
  return (await axios.put(`${API}/admin/engagement/alert-settings`, payload, { headers: authHeaders() })).data;
}
export async function engagementNotifyNow() {
  return (await axios.post(`${API}/admin/engagement/notify-now`, {}, { headers: authHeaders() })).data;
}
export async function campaignsList() {
  return (await axios.get(`${API}/admin/campaigns`, { headers: authHeaders() })).data;
}
export async function campaignCreate(payload) {
  return (await axios.post(`${API}/admin/campaigns`, payload, { headers: authHeaders() })).data;
}
export async function campaignUpdate(id, payload) {
  return (await axios.patch(`${API}/admin/campaigns/${id}`, payload, { headers: authHeaders() })).data;
}
export async function campaignDelete(id) {
  return (await axios.delete(`${API}/admin/campaigns/${id}`, { headers: authHeaders() })).data;
}
export async function subsDetectIndustries(all = false) {
  return (await axios.post(`${API}/admin/subscriptions/detect-industries?all=${all}`, {}, { headers: authHeaders() })).data;
}
export async function subsBulkPreview(payload) {
  return (await axios.post(`${API}/admin/subscriptions/bulk-email/preview`, payload, { headers: authHeaders() })).data;
}
export async function subsBulkDraft(payload) {
  return postWithRetry(`${API}/admin/subscriptions/bulk-email/draft`, payload);
}
export async function subsBulkSend(payload) {
  return postWithRetry(`${API}/admin/subscriptions/bulk-email/send`, payload);
}
export async function exportPageviews(params) {
  const res = await axios.get(`${API}/admin/analytics/export`, { params, headers: authHeaders(), responseType: 'blob' });
  return res.data;
}

// ---- Support / Disputes inbox ----
export async function createSupportTicket(payload) {
  return (await axios.post(`${API}/support`, payload)).data;
}
export async function supportStats() {
  return (await axios.get(`${API}/admin/support/stats`, { headers: authHeaders() })).data;
}
export async function supportList(params) {
  return (await axios.get(`${API}/admin/support`, { params, headers: authHeaders() })).data;
}
export async function supportGet(id) {
  return (await axios.get(`${API}/admin/support/${id}`, { headers: authHeaders() })).data;
}
export async function supportUpdate(id, body) {
  return (await axios.patch(`${API}/admin/support/${id}`, body, { headers: authHeaders() })).data;
}
export async function supportDraft(id, body) {
  return (await axios.post(`${API}/admin/support/${id}/draft`, body, { headers: authHeaders() })).data;
}
export async function supportReply(id, body) {
  return (await axios.post(`${API}/admin/support/${id}/reply`, body, { headers: authHeaders() })).data;
}
export async function supportHide(id, reason) {
  return (await axios.post(`${API}/admin/support/${id}/hide`, { reason }, { headers: authHeaders() })).data;
}
export async function supportRestore(id) {
  return (await axios.post(`${API}/admin/support/${id}/restore`, {}, { headers: authHeaders() })).data;
}
export async function supportDelete(id) {
  return (await axios.delete(`${API}/admin/support/${id}`, { headers: authHeaders() })).data;
}
export async function graphHealth() {
  return (await axios.get(`${API}/admin/integrations/graph/health`, { headers: authHeaders() })).data;
}
export async function inboxFeed() {
  return (await axios.get(`${API}/admin/inbox/feed`, { headers: authHeaders() })).data;
}

// ---- Tech Config: sync API keys ----
export async function techKeys() {
  return (await axios.get(`${API}/admin/tech-config/keys`, { headers: authHeaders() })).data;
}
export async function techCreateKey(name) {
  return (await axios.post(`${API}/admin/tech-config/keys`, { name }, { headers: authHeaders() })).data;
}
export async function techToggleKey(id) {
  return (await axios.post(`${API}/admin/tech-config/keys/${id}/toggle`, {}, { headers: authHeaders() })).data;
}
export async function techDeleteKey(id) {
  return (await axios.delete(`${API}/admin/tech-config/keys/${id}`, { headers: authHeaders() })).data;
}
export async function techSyncSettings() {
  return (await axios.get(`${API}/admin/tech-config/sync-settings`, { headers: authHeaders() })).data;
}
export async function techSaveSyncSettings(syncStartDate) {
  return (await axios.put(`${API}/admin/tech-config/sync-settings`, { sync_start_date: syncStartDate }, { headers: authHeaders() })).data;
}
export async function techStatus() {
  return (await axios.get(`${API}/admin/tech-config/status`, { headers: authHeaders() })).data;
}
export async function techPurge(password, targets) {
  return (await axios.post(`${API}/admin/tech-config/purge`, { password, targets }, { headers: authHeaders() })).data;
}

// ---- CAPTCHA (anti-spam) config ----
export async function getCaptchaSettings() {
  return (await axios.get(`${API}/admin/settings/captcha`, { headers: authHeaders() })).data;
}
export async function saveCaptchaSettings(body) {
  return (await axios.put(`${API}/admin/settings/captcha`, body, { headers: authHeaders() })).data;
}
export async function publicCaptchaConfig() {
  return (await axios.get(`${API}/captcha/config`)).data;
}

// ---- Microsoft 365 calendar (delegated per-rep) ----
export async function calendarStatus() {
  return (await axios.get(`${API}/admin/calendar/status`, { headers: authHeaders() })).data;
}
export async function calendarConnect() {
  return (await axios.get(`${API}/admin/calendar/connect`, { headers: authHeaders() })).data;
}
export async function calendarAdminConsent() {
  return (await axios.get(`${API}/admin/calendar/admin-consent`, { headers: authHeaders() })).data;
}
export async function calendarDisconnect() {
  return (await axios.post(`${API}/admin/calendar/disconnect`, {}, { headers: authHeaders() })).data;
}
export async function calendarReps() {
  return (await axios.get(`${API}/admin/calendar/reps`, { headers: authHeaders() })).data;
}
export async function calendarAvailability(payload) {
  return (await axios.post(`${API}/admin/calendar/availability`, payload, { headers: authHeaders() })).data;
}
export async function calendarBook(payload) {
  return postWithRetry(`${API}/admin/calendar/book`, payload);
}

// ---- Enquiry email (AI draft + send via rep's connected M365 mailbox) ----
export async function enquiryEmailDraft(leadId, payload) {
  return postWithRetry(`${API}/admin/enquiries/${leadId}/email/draft`, payload);
}
export async function enquiryEmailSend(leadId, payload) {
  return postWithRetry(`${API}/admin/enquiries/${leadId}/email/send`, payload);
}

// ---- Platform notifications ----
export async function notifRecipients(segment) {
  return (await axios.get(`${API}/admin/notifications/recipients`, { params: segment ? { segment } : {}, headers: authHeaders() })).data;
}
export async function sendNotification(body) {
  return (await axios.post(`${API}/admin/notifications/send`, body, { headers: authHeaders() })).data;
}
export async function listNotifications() {
  return (await axios.get(`${API}/admin/notifications`, { headers: authHeaders() })).data;
}

